import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { DEFAULT_ROOM } from "../constants/gameStages";
import { SocketContext } from "../context/SocketProvider";
import { useGameDataSync } from "../hooks/useGameDataSync";
import { useSettings } from "../context/SettingsContext";
import { useRoleBackground } from "../hooks/useRoleBackground";
import { useLanguage } from "../context/LanguageContext";
import { FaChevronRight } from "react-icons/fa6";
import lose1_en from "../assets/results_lose1_r.png";
import lose2_en from "../assets/results_lose2_r.png";
import lose3_en from "../assets/results_lose3_r.png";
import win_en from "../assets/results_win_r.png";
import lose1_jp from "../assets/results_lose1_jp.png";
import lose2_jp from "../assets/results_lose2_jp.png";
import lose3_jp from "../assets/results_lose3_jp.png";
import win_jp from "../assets/results_win_jp.png";
import describerIconPng from "../assets/describer_icon.png";
import guesserIconPng from "../assets/guesser_icon.png";
import aicomIconPng from "../assets/AICOMchan_icon.png";
import correct from "../assets/results_correct.png";
import incorrect from "../assets/results_incorrect.png";

function SeeResults() {
  const { socket } = useContext(SocketContext);
  const { imageCount } = useSettings();
  const { t, language } = useLanguage();

  const { allImages, gameData } = useGameDataSync();
  const guessResult = gameData?.guessResult;
  const description = guessResult?.description || "";
  const [aiResult, setAiResult] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(true);
  const { bgStyle, className } = useRoleBackground("yellow");

  useEffect(() => {
    const handleResultReady = (data) => {
      if (data && data.success && allImages.length === imageCount) {
        setAiResult({
          guessedImageIndex: data.best_match_image,
          guessedImage: data.best_match_image >= 0 ? allImages[data.best_match_image] : null,
          reason: data.reason,
        });
      } else {
        console.error(
          "AI guess failed due to image mismatch or server error:",
          {
            serverResponse: data,
            imageCount,
            allImagesLength: allImages.length,
          }
        );
      }
      setIsLoadingAI(false);
    };

    socket.on("result_ready", handleResultReady);
    socket.emit("get_result", { room: DEFAULT_ROOM });

    // Clean up on unmount
    return () => {
      socket.off("result_ready", handleResultReady);
    };
  }, [allImages]); // Reacts to updated allImages

  if (!guessResult || allImages.length !== imageCount || isLoadingAI) {
    return (
      <div
        className={`min-h-screen flex flex-col justify-center items-center ${className}`}
        style={bgStyle}
      >
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-8">
            {t("waiting.loading", "Loading results...")}
          </h1>
        </div>
      </div>
    );
  }
  // Extract indices for clarity
  const descriptorSelectedIndex = guessResult?.originalImageIndex;
  const guesserSelectedIndex = guessResult?.guessedImageIndex;
  const playerDescribedImage = allImages[descriptorSelectedIndex];
  const playerGuessedImage = typeof guesserSelectedIndex === 'number' && guesserSelectedIndex >= 0
    ? allImages[guesserSelectedIndex]
    : null;
  const isPlayerCorrect = guesserSelectedIndex === descriptorSelectedIndex;
  const isAICorrect = aiResult
    ? aiResult.guessedImageIndex === descriptorSelectedIndex
    : false;

  // Select appropriate images based on language
  const resultImages =
  language === "ja"
    ? {
        bothCorrect: lose1_jp,    // results_lose1_r
        aiWins: lose3_jp,         // results_lose3_r
        playerWins: win_jp,       // results_win_r
        bothWrong: lose2_jp,      // results_lose2_r
        noGuess: lose1_jp,        // results_lose1_r
      }
    : {
        bothCorrect: lose1_en,    // results_lose1_r
        aiWins: lose3_en,         // results_lose3_en
        playerWins: win_en,       // results_win_en
        bothWrong: lose2_en,      // results_lose2_en
        noGuess: lose1_en,        // results_lose1_en
      };

  const getStatusKey = () => {
    if (isPlayerCorrect && isAICorrect) return "results.guesserCorrectAiNotTricked";
    if (isPlayerCorrect) return "results.guesserCorrectAiTricked";
    if (isAICorrect) return "results.guesserWrongAiCorrect";
    return "results.guesserWrongAiTricked";
  };

  // Parser for the color tags in the translation strings
  const renderFormattedText = (key) => {
    const rawText = t(key);
    if (!rawText) return "";

    return rawText.split(/(<[02]>.*?<\/[02]>)/g).map((part, i) => {
      if (part.startsWith("<0>")) {
        // Color aicom_blue (#109EFF)
        return <span key={i} className="text-aicom_blue">{part.replace(/<\/?0>/g, "")}</span>;
      }
      if (part.startsWith("<2>")) {
        // Color aicom_yellow (#FCED00)
        return <span key={i} className="text-yellow400">{part.replace(/<\/?2>/g, "")}</span>;
      }
      return part;
    });
  };
        
  const getBorderStyle = (index) => {
    const colors = [];
    if (index === descriptorSelectedIndex) colors.push("#FF52C0");
    if (index === guesserSelectedIndex) colors.push("#109EFF");
    if (index === aiResult?.guessedImageIndex) colors.push("#FCED00");
    if (colors.length === 0) return {};

    const gradient = colors.length === 1 
      ? `linear-gradient(${colors[0]}, ${colors[0]})`
      : colors.length === 2 
        ? `linear-gradient(to right, ${colors[0]} 50%, ${colors[1]} 50%)`
        : `linear-gradient(to right, ${colors[0]} 33%, ${colors[1]} 33%, ${colors[1]} 66%, ${colors[2]} 66%)`;

    return {
      padding: "6px", 
      background: gradient,
      borderRadius: "15px"
    };
  };

  return (
    <div
      className={`p-4 md:p-8 lg:p-[64px] flex flex-col items-center min-h-screen ${className}`}
      style={bgStyle}
    >
      <div className="large-card pb-4">
        <h2 className="large-card-title bg-yellow400 py-[11px]">
          {t("results.title", "RESULTS:")}
        </h2>

        {!isLoadingAI && (
          <div className="mt-[18px] flex flex-col items-center">
            <div className="flex items-center gap-4">
              <img 
                src={isPlayerCorrect && !isAICorrect ? correct : incorrect} 
                alt="status" 
                className="w-[51px] h-[51px]" 
              />
              <h2 className="text-[40px] font-black uppercase">
                {isPlayerCorrect && !isAICorrect ? t("results.youWin") : t("results.youLose")}
              </h2>
            </div>
            
            <div className="mt-[8px] text-[18px] font-bold text-center max-w-[700px]">
              {renderFormattedText(getStatusKey())} 
            </div>
          </div>
        )}




        {/* Middle container */}
        <div className="flex flex-row items-start justify-between w-full mt-[30px] px-[20px] gap-2">

          {/* Left container */}
          <div className="flex flex-col flex-[0_0_596px] h-[292px]">

            {/* Photos container */}
            <div className={`grid gap-x-[12px] gap-y-[12px] pl-[2px] pr-[2px] py-[10px] place-items-center ${
                imageCount === 6 ? "grid-cols-3" : "grid-cols-4"
              }`}
            >
              {allImages.map((image, index) => {
                
                const playerIDLabel =
                  index === descriptorSelectedIndex
                    ? t("results.describer", "DESCRIBER")
                    : index === guesserSelectedIndex
                    ? t("results.guesser", "GUESSER")
                    : index === aiResult?.guessedImageIndex
                    ? t("results.aicom", "AICOM-CHAN")
                    : "";

                const imageOpacityClass = !playerIDLabel ? "opacity-60" : "";

                const hasDescriber = index === descriptorSelectedIndex;
                const hasGuesser = index === guesserSelectedIndex;
                const hasAI = index === aiResult?.guessedImageIndex;
                const tabs = [hasDescriber && 'describer', hasGuesser && 'guesser', hasAI && 'ai'].filter(Boolean);
                const tabCount = tabs.length;
                
                const getMlClass = (tabIndex) => {
                  if (tabCount === 1) return "ml-0";
                  if (tabCount === 2) {
                    return tabIndex === 0 ? "ml-0" : "ml-[-13px]"; // 2nd tab offset
                  }
                  // tabCount === 3
                  return tabIndex === 0 ? "ml-0" : 
                        tabIndex === 1 ? (language === "ja" ? "ml-[-10px]" : "ml-[-40px]") : "ml-[-14px]";
                };

                const borderStyle = getBorderStyle(index);
                  const isSelected = index === descriptorSelectedIndex || 
                                    index === guesserSelectedIndex || 
                                    index === aiResult?.guessedImageIndex;

                return (
                <div key={index} className="relative flex flex-col items-center">
                  <div className="absolute -top-[44.25px] left-1/2 -translate-x-1/2 flex items-center z-10">

                    {/* 1. DESCRIBER (leftmost, top layer z-30) */}
                    {index === descriptorSelectedIndex && (
                      <div className={`flex flex-col items-center z-30 ${getMlClass(0)}`}>
                        <img src={describerIconPng} alt="" className="w-[36px] h-[36px] mb-[-6px]" /> 
                        <h2 className="text-[11px] font-bold px-[4px] py-[2px] rounded-t-[6px] text-black bg-aicom_pink min-w-[50px] text-center"> {/* a modifier */}
                          {t("results.describer", "DESCRIBER")}
                        </h2>
                      </div>
                    )}

                    {/* 2. GUESSER (middle, z-20) */}
                    {index === guesserSelectedIndex && (
                      <div className={`flex flex-col items-center z-20 ${getMlClass(hasDescriber ? 1 : 0)}`}>
                        <img src={guesserIconPng} alt="" className="w-[36px] h-[36px] mb-[-6px]" />
                      <h2 className="text-[11px] font-bold px-[4px] py-[2px] rounded-t-[6px] text-black bg-aicom_blue min-w-[30px] text-center whitespace-nowrap">
                          {t("results.guesser", "GUESSER")}
                        </h2>
                      </div>
                    )}

                    {/* 3. AICOM (rightmost, bottom layer z-10) */}
                    {index === aiResult?.guessedImageIndex && (
                      <div className={`flex flex-col items-center z-10 ${getMlClass((hasDescriber ? 1 : 0) + (hasGuesser ? 1 : 0))}`} >
                        <img src={aicomIconPng} alt="" className="w-[47px] h-[36px] mb-[-6px]" />
                        <h2 className="text-[11px] font-bold px-[4px] py-[2px] rounded-t-[6px] text-black bg-aicom_yellow min-w-[30px] text-center">
                          {t("AICOM", "AICOM")}
                        </h2>
                      </div>
                    )}
                  </div>

                  <div 
                    className={`size-[140px] rounded-[15px] overflow-hidden z-5 ${!isSelected ? "opacity-60" : ""}`}
                    style={borderStyle}
                  >
                    <img
                      src={image.url}
                      alt={`Image ${index + 1}`}
                      className="size-full object-cover rounded-[10px]" 
                    />
                  </div>
                </div>
                );
              })}
            </div>

            {/* No guess container*/}
            <div className="flex flex-row items-center justify-end mt-2 pr-2 gap-4">
              <span className="font-black text-[18px] uppercase">{t("results.noGuess", "NO GUESS:")}</span>
              
              <div className="flex items-center gap-2">
                {!(typeof guesserSelectedIndex === 'number' && guesserSelectedIndex >= 0) && (
                  <div className="flex flex-col items-center">
                    <img src={guesserIconPng} alt="Guesser Icon" className="w-[36px] h-[36px] mb-[-6px]" />
                    <h2 className="text-[10px] font-bold px-[6px] py-[1px] rounded-full text-black bg-aicom_blue uppercase">
                      {t("results.guesser", "GUESSER")}
                    </h2>
                  </div>
                )}
                {!(typeof aiResult?.guessedImageIndex === 'number' && aiResult.guessedImageIndex >= 0) && (
                  <div className="flex flex-col items-center">
                    <img src={aicomIconPng} alt="AICOM Icon" className="w-[45px] h-[36px] mb-[-6px]" />
                    <h2 className="text-[10px] font-bold px-[6px] py-[1px] rounded-full text-black bg-aicom_yellow uppercase">
                      {t("AICOM", "AICOM")}
                    </h2>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right container */}
          <div className="flex flex-col flex-1 max-w-[384px]"> 

            {/* Text containers */}
            <div className="flex flex-col gap-[12px] h-[292px] mt-[10px]">

              {/* Pink box */}
              <div className="flex-1 border-[4px] border-aicom_pink rounded-[14px] p-3 bg-white shadow-sm flex flex-col ">
                <h3 className="font-black text-[17px] mb-1 uppercase text-left">
                  {t("results.describerSentence", "DESCRIBER'S SENTENCE:")}
                </h3>
                <p className="text-[15px] font-normal text-black text-left leading-[20px]">
                  "{description}"
                </p>
              </div>

              {/* Yellow box */}
              <div className="flex-1 border-[4px] border-aicom_yellow rounded-[14px] p-3 bg-white shadow-sm flex flex-col">
                <h3 className="font-black text-[15px] mb-1 uppercase text-left">
                  {t("results.aicomReason", "AICOM-CHAN'S REASON:")}
                </h3>
                <p className={`text-[15px] font-normal text-left ${!aiResult ? "text-red-600" : "text-black"}`}>
                  "{aiResult ? aiResult.reason : t("error.failedGetAiGuess", "Failed to get AI Guess")}"
                </p>
              </div>
            </div>

            {/* Bottom container */}
            <div className="flex justify-end mt-6">
              <Link to="/points">
                <button className="btn-results-sm relative">
                  <span className="btn-text-pos-right-sm">
                    {t("button.seePoints", "See Points")}
                  </span>
                  <FaChevronRight className="arrow-pos-right-sm" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <img
        className="fixed bottom-0 left-[37px] w-[250px] z-[9999] pointer-events-none"
        alt="aicom icon"
        src={
          !(typeof guesserSelectedIndex === 'number' && guesserSelectedIndex >= 0) &&
          !(typeof aiResult?.guessedImageIndex === 'number' && aiResult.guessedImageIndex >= 0)
            ? resultImages.noGuess // results_lose1_r

          : isPlayerCorrect && isAICorrect
            ? resultImages.bothCorrect // results_lose1_r

          : isPlayerCorrect && !isAICorrect
            ? resultImages.playerWins // results_win_r

          : !isPlayerCorrect && isAICorrect
            ? resultImages.aiWins // results_lose3_r

          : resultImages.bothWrong // results_lose2_r
        }
      />
    </div>
  );
}

export default SeeResults;
